import { BookOpen, Lightbulb, Quote, TriangleAlert as AlertTriangle, Sparkles } from 'lucide-react';
import type { TabCoreConcept } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';
import { sanitizeUrl } from '../../../../lib/sanitizeUrl';

interface Props {
  data: TabCoreConcept;
}

const CALLOUT_STYLES = {
  tip: { bg: 'bg-app-positive/10', border: 'border-app-positive', text: 'text-app-positive', icon: Lightbulb, label: 'Pro Tip' },
  warning: { bg: 'bg-app-warning/10', border: 'border-app-warning', text: 'text-app-warning', icon: AlertTriangle, label: 'Warning' },
  example: { bg: 'bg-app-focus/10', border: 'border-app-focus/30', text: 'text-app-focus', icon: Quote, label: 'Example' },
  key_insight: { bg: 'bg-forest-50', border: 'border-forest-200', text: 'text-forest-700', icon: Sparkles, label: 'Key Insight' },
};

export default function CoreConceptTab({ data }: Props) {
  if (!data?.overview && !data?.sections?.length) {
    return <EmptyState tabName="Core Concept" />;
  }

  return (
    <div className="space-y-6">
      {data.overview && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Overview</h4>
          </div>
          <FormattedText text={data.overview} className="text-sm text-app-ink" />
        </div>
      )}

      {data.sections?.length > 0 && (
        <div className="space-y-5">
          {data.sections.map((section, i) => (
            <div key={i}>
              <h4 className="text-sm font-semibold text-app-ink mb-2">{section.heading}</h4>
              <FormattedText text={section.body} className="text-sm text-app-ink" />
              {section.callout_type && section.callout_text && (
                <CalloutBox type={section.callout_type} text={section.callout_text} />
              )}
            </div>
          ))}
        </div>
      )}

      {data.key_principles?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Key Principles</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.key_principles.map((p, i) => (
              <div key={i} className="flex items-start gap-2 bg-app-surface border border-app-line rounded-lg p-3">
                <span className="w-5 h-5 rounded-full bg-app-focus/10 text-app-focus flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-app-ink">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.citations?.length > 0 && (
        <div className="border-t border-app-line pt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-app-muted mb-2">Sources & Citations</h4>
          <div className="space-y-1.5">
            {data.citations.map((c, i) => (
              <p key={i} className="text-xs text-app-muted">
                {c.author} ({c.year}). <span className="italic">{c.title}</span>. {c.publication}.
                {c.url && (
                  <a href={sanitizeUrl(c.url)} target="_blank" rel="noopener noreferrer" className="text-app-focus hover:underline ml-1">Link</a>
                )}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CalloutBox({ type, text }: { type: string; text: string }) {
  const style = CALLOUT_STYLES[type as keyof typeof CALLOUT_STYLES] || CALLOUT_STYLES.tip;
  const Icon = style.icon;
  return (
    <div className={`mt-3 ${style.bg} border ${style.border} rounded-lg px-4 py-3`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${style.text}`} />
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${style.text}`}>{style.label}</span>
      </div>
      <p className={`text-xs ${style.text} leading-relaxed`}>{text}</p>
    </div>
  );
}

function EmptyState({ tabName }: { tabName: string }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-app-muted">No {tabName} data available yet.</p>
      <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
    </div>
  );
}
